import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

// Global axios interceptor for 401 handling - auto-refresh token
const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/logout', '/auth/me', '/auth/refresh'];

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    
    // Don't intercept auth endpoints themselves to avoid loops
    const isAuthEndpoint = AUTH_PATHS.some(p => requestUrl.includes(p));
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return axios(originalRequest);
      } catch {
        // Refresh failed - redirect to login
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      // Cache user pour usage offline
      try { localStorage.setItem('mdb_cached_user', JSON.stringify(response.data)); } catch { /* ignore */ }
    } catch (err) {
      // En mode hors-ligne (ou si /auth/me indisponible), on accepte l'utilisateur précédemment authentifié
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        try {
          const cached = localStorage.getItem('mdb_cached_user');
          if (cached) {
            setUser(JSON.parse(cached));
            return;
          }
        } catch { /* ignore */ }
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data);
    try { localStorage.setItem('mdb_cached_user', JSON.stringify(response.data)); } catch { /* ignore */ }
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    setUser(response.data);
    try { localStorage.setItem('mdb_cached_user', JSON.stringify(response.data)); } catch { /* ignore */ }
    return response.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
    try { localStorage.removeItem('mdb_cached_user'); } catch { /* ignore */ }
    setUser(false);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
